import type { SiegeCampaignNode, SiegeRegion } from "./siege-campaign";

export interface SiegeMapClearEntry {
  map_key: string;
  best_stars: number;
}

/** Maps in a region that have at least one victory. */
export const regionClearedCount = (
  regionId: string,
  nodes: SiegeCampaignNode[],
  clears: SiegeMapClearEntry[],
): { cleared: number; total: number } => {
  const maps = nodes.filter((n) => n.region_id === regionId);
  const clearedKeys = new Set(clears.map((c) => c.map_key));
  return {
    cleared: maps.filter((n) => clearedKeys.has(n.map_key)).length,
    total: maps.length,
  };
};

/** Region is cleared when every map in it has a victory. */
export const isRegionCleared = (
  regionId: string,
  nodes: SiegeCampaignNode[],
  clears: SiegeMapClearEntry[],
): boolean => {
  const { cleared, total } = regionClearedCount(regionId, nodes, clears);
  return total > 0 && cleared === total;
};

/**
 * Region unlock: all `unlocked_by` parents must be fully cleared.
 * Starter regions (`unlocked_by: []`) are always unlocked.
 */
export const isRegionUnlocked = (
  region: SiegeRegion,
  nodes: SiegeCampaignNode[],
  clears: SiegeMapClearEntry[],
): boolean => {
  if (!region.unlocked_by.length) return true;
  return region.unlocked_by.every((parentId) =>
    isRegionCleared(parentId, nodes, clears),
  );
};

/**
 * Average best_stars across maps in the region.
 * Uncleared maps count as 0. Returns 0 when the region has no maps.
 */
export const regionAverageStars = (
  regionId: string,
  nodes: SiegeCampaignNode[],
  clears: SiegeMapClearEntry[],
): number => {
  const maps = nodes.filter((n) => n.region_id === regionId);
  if (maps.length === 0) return 0;
  const byKey = new Map(clears.map((c) => [c.map_key, c.best_stars]));
  const sum = maps.reduce((acc, n) => acc + (byKey.get(n.map_key) ?? 0), 0);
  return sum / maps.length;
};

/** Format average stars for UI (one decimal). */
export const formatRegionStars = (avg: number): string => avg.toFixed(1);
